import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, Bell, FileText, DollarSign, CheckCircle, Camera, 
  ExternalLink, Calendar, MessageSquare, Image, ClipboardList,
  Shield, Clock, Play, ChevronRight, ArrowLeft, HelpCircle
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { companyInfo } from '../data/mock';

const JobTreadPage = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  const customerBenefits = [
    {
      icon: Clock,
      title: "Track Progress in Real-Time",
      description: "Monitor the evolution of your project as new updates happen, without needing to be on-site.",
      image: "https://www.jobtread.com/images/resources/marketing/customer-kit/jobtread-software-customer-daily-logs.png"
    },
    {
      icon: DollarSign,
      title: "Ensure Financial Transparency",
      description: "Review and sign proposals, view invoices, and make payments for the work performed.",
      image: "https://www.jobtread.com/images/resources/marketing/customer-kit/jobtread-software-customer-e-signatures.png"
    },
    {
      icon: MessageSquare,
      title: "Improve Communication",
      description: "Keep everyone on the same page and eliminate the need for multiple communication channels.",
      image: "https://www.jobtread.com/images/resources/marketing/customer-kit/jobtread-software-customer-communications.png"
    },
    {
      icon: FileText,
      title: "Centralize Project Information",
      description: "Quickly access all documents, photos, and other project related information in one location.",
      image: "https://www.jobtread.com/images/resources/marketing/customer-kit/jobtread-software-customer-document-list.png"
    }
  ];

  const portalFeatures = [
    {
      icon: Users,
      title: "Customer Portal",
      description: "Access your dedicated project portal anytime. View documents, track progress, approve selections, and communicate directly with our team.",
      screenshot: "https://www.jobtread.com/images/resources/marketing/customer-kit/jobtread-software-responsive-devices.png"
    },
    {
      icon: Calendar,
      title: "Scheduling",
      description: "Have full visibility into your construction project's schedule every step of the way. No more guesswork or waiting for updates—easily log in anytime to see what's been completed, what's coming next, and when major milestones are expected.",
      screenshot: "https://www.jobtread.com/images/resources/marketing/customer-kit/jobtread-software-customer-scheduling.png"
    },
    {
      icon: ClipboardList,
      title: "Daily Logs",
      description: "Stay in the loop about what's happening on the construction site, even when you can't be there in person. View detailed updates on the progress made, tasks completed, and any notes from the crew.",
      screenshot: "https://www.jobtread.com/images/resources/marketing/customer-kit/jobtread-software-customer-daily-logs.png"
    },
    {
      icon: Image,
      title: "Progress Photos",
      description: "Visually track the transformation of your construction project, no matter where you are, with easy to access photos detailing job progress.",
      screenshot: "https://www.jobtread.com/images/resources/marketing/customer-kit/jobtread-software-customer-view-progress-photos.png"
    },
    {
      icon: CheckCircle,
      title: "Selections & Approvals",
      description: "Managing choices for your project has never been easier. From materials and finishes to fixtures and colors, view all of your selections in one place, keeping everything organized and clear.",
      screenshot: "https://www.jobtread.com/images/resources/marketing/customer-kit/jobtread-software-customer-selections.png"
    },
    {
      icon: FileText,
      title: "View Job Documents",
      description: "Quickly access all documents, photos, and other project-related information in one location. This can include estimates, change orders, and invoices.",
      screenshot: "https://www.jobtread.com/images/resources/marketing/customer-kit/jobtread-software-customer-document-list.png"
    }
  ];

  const helpResources = [
    { title: "Using The Customer Portal", url: "https://www.jobtread.com/resources/help/customer-portal" },
    { title: "Create a Password", url: "https://www.jobtread.com/resources/help/customer-portal/create-a-password" },
    { title: "Mobile App Guide", url: "https://www.jobtread.com/resources/help/customer-portal/jobtread-mobile-app" },
    { title: "Sign a Proposal", url: "https://www.jobtread.com/resources/help/customer-portal/sign-a-proposal" },
    { title: "Pay an Invoice", url: "https://www.jobtread.com/resources/help/customer-portal/pay-an-invoice" },
    { title: "Send a Message", url: "https://www.jobtread.com/resources/help/customer-portal/send-a-message" },
    { title: "Make Selections", url: "https://www.jobtread.com/resources/help/customer-portal/make-selections" },
    { title: "Customize Notifications", url: "https://www.jobtread.com/resources/help/customer-portal/customize-notifications" }
  ];

  const faqs = [
    {
      question: "Do I need to pay for a portal?",
      answer: "No, you don't need to pay for a portal. The customer portal is provided to you and your contractor at no cost."
    },
    {
      question: "Do I need to create a login to access my portal?",
      answer: "In most cases, you will not need a username and password to access your portal; you can simply click the link in any of your emails and the portal automatically opens. However, a password is required for added security if you add JobTread as a mobile app or if you plan to make payments through your portal."
    },
    {
      question: "What if I have more than one project?",
      answer: "As soon as you have more than one project with us, a main navigation panel will appear on your portal. This allows you to switch between projects and view tasks and documents for all projects in one place."
    },
    {
      question: "Is there training available?",
      answer: "Yes! JobTread provides comprehensive training resources including video tutorials and step-by-step guides to help you get the most out of your customer portal."
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-52 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-1/4 w-64 h-64 bg-orange-500 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-cyan-400 hover:text-cyan-300 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://www.jobtread.com/images/resources/marketing/were-better-with-jobtread.svg" 
                alt="We're Better with JobTread" 
                className="h-20 mb-8"
              />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Your Project, <span className="text-cyan-400">Your Portal</span>
              </h1>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                We use JobTread construction management software to give you complete transparency into your project. Access real-time updates, financial insights, and seamless communication—all in one place.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href={companyInfo.jobtreadLoginUrl} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-8">
                    Access Your Portal
                    <ExternalLink className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <a href="https://www.jobtread.com/resources/help/customer-portal" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 font-semibold px-8">
                    Get Help
                    <HelpCircle className="ml-2 w-5 h-5" />
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://www.jobtread.com/images/resources/marketing/customer-kit/jobtread-software-responsive-devices.png"
                alt="JobTread on multiple devices"
                className="w-full rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why JobTread Benefits */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center bg-cyan-500/10 text-cyan-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Why We Use JobTread
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              A Better Experience for You
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              JobTread transforms the construction experience for homeowners. With real-time updates, clear financial insights, and centralized communication, your project management is simplified from start to finish.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {customerBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="group bg-slate-50 rounded-2xl p-8 hover:bg-white hover:shadow-xl transition-all duration-300 border border-slate-100">
                  <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-cyan-500 transition-colors">
                    <Icon className="w-7 h-7 text-cyan-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Portal Features Showcase */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center bg-cyan-500/10 text-cyan-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Portal Features
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Everything You Need in One Place
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Your JobTread customer portal puts all project information at your fingertips. Here's what you can do:
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Feature Navigation */}
            <div className="lg:col-span-1 space-y-3">
              {portalFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <button
                    key={index}
                    onClick={() => setActiveFeature(index)}
                    className={`w-full text-left p-4 rounded-xl transition-all duration-300 flex items-center gap-4 ${
                      activeFeature === index 
                        ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/30' 
                        : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className={`w-6 h-6 flex-shrink-0 ${activeFeature === index ? 'text-white' : 'text-cyan-600'}`} />
                    <span className="font-semibold">{feature.title}</span>
                    <ChevronRight className={`w-5 h-5 ml-auto ${activeFeature === index ? 'text-white' : 'text-slate-400'}`} />
                  </button>
                );
              })}
            </div>

            {/* Feature Detail */}
            <div className="lg:col-span-2">
              <div className="bg-slate-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  {portalFeatures[activeFeature].title}
                </h3>
                <p className="text-slate-600 mb-8 text-lg">
                  {portalFeatures[activeFeature].description}
                </p>
                <div className="rounded-xl overflow-hidden shadow-xl border border-slate-200">
                  <img 
                    src={portalFeatures[activeFeature].screenshot}
                    alt={portalFeatures[activeFeature].title}
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Start Guide / Video Tutorial Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-flex items-center bg-cyan-500/10 text-cyan-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Quick Start Guide
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Learn How to Use Your Customer Portal
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Watch this quick tutorial to learn how to navigate your JobTread customer portal. In just a few minutes, you'll understand how to track progress, view documents, communicate with our team, and more.
              </p>
              <ul className="space-y-4 mb-8">
                {['Access your portal from any device', 'View real-time project updates', 'Review and sign documents digitally', 'Track payments and invoices'].map((item, i) => (
                  <li key={i} className="flex items-center text-slate-700">
                    <CheckCircle className="w-5 h-5 text-cyan-600 mr-3 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <a 
                href="https://vimeo.com/931164575" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-cyan-600 hover:text-cyan-700 font-semibold"
              >
                Watch on Vimeo
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
            <div className="relative">
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl bg-slate-900">
                <iframe 
                  src="https://player.vimeo.com/video/931164575?h=47af837853&color=ff5600&title=0&byline=0&portrait=0" 
                  className="w-full h-full"
                  allow="autoplay; fullscreen; picture-in-picture" 
                  allowFullScreen
                  title="Using the Customer Portal"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-cyan-500/20 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Help Resources */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center bg-cyan-500/10 text-cyan-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Training & Support
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Help When You Need It
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Access step-by-step guides and tutorials to help you get the most out of your customer portal.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {helpResources.map((resource, index) => (
              <a
                key={index}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-cyan-300 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-900 group-hover:text-cyan-600 transition-colors">
                    {resource.title}
                  </span>
                  <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-cyan-600 transition-colors" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-cyan-600 font-semibold text-sm tracking-wider uppercase mb-4">
              FAQ
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-3">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Access Your Project?
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-10">
              Log in to your customer portal to track progress, view documents, and stay connected with your project team.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={companyInfo.jobtreadLoginUrl} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-10 py-6 text-lg">
                  Customer Portal
                  <ExternalLink className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <a href={companyInfo.jobtreadLoginUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 font-semibold px-10 py-6 text-lg">
                  Vendor Portal
                  <ExternalLink className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JobTreadPage;
